import { pomegranateAuthAtom } from '@/atoms/pomegranate'
import { accountAtom, bunkerLogin } from '@/atoms/session'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import SecondaryPageLayout from '@/layouts/SecondaryPageLayout'
import {
  authenticateWithGoogle,
  getLocalSecretKey,
  pomegranateSetup,
  TRY_AGAIN
} from '@/services/google-login.service'
import { aggregateSecretKeyShards, decodeShard } from '@fiatjaf/promenade-trusted-dealer'
import { hexToBytes } from '@noble/hashes/utils.js'
import { numberToBytesBE } from '@noble/curves/utils.js'
import { npubEncode, nsecEncode } from '@nostr/tools/nip19'
import { getPublicKey } from '@nostr/tools/wasm'
import { HugeiconsIcon } from '@hugeicons/react'
import { CheckIcon } from '@hugeicons/core-free-icons'
import { useAtomValue, useSetAtom } from 'jotai'
import { forwardRef, useCallback, useEffect, useMemo, useState } from 'react'
import { toast } from 'sonner'

const CENTRAL_OPTIONS = ['https://auth.njump.me', 'https://auth.yakihonne.com']

type AccountOperator = {
  url: string
  pubshard: string
}

type Account = {
  email: string
  pubkey: string
  operators: AccountOperator[]
  threshold: number
}

type Profile = {
  handler_pubkey: string
  name: string
  email: string
}

function massageURL(input: string): string {
  let url = input.trim()
  if (!url.startsWith('http')) {
    url = 'http' + (url.startsWith('localhost') ? '' : 's') + '://' + url
  }
  return new URL(url).origin
}

const PomegranatePage = forwardRef(({ index }: { index?: number }, ref) => {
  const pomegranateAuth = useAtomValue(pomegranateAuthAtom)
  const setPomegranateAuth = useSetAtom(pomegranateAuthAtom)
  const currentAccount = useAtomValue(accountAtom)

  const currentBunkerPubkey = useMemo(() => {
    try {
      const bunkerUrl = new URL(currentAccount?.bunker ?? '')
      return bunkerUrl.hostname
    } catch {
      return null
    }
  }, [currentAccount])

  const [account, setAccount] = useState<Account | null>(null)
  const [profiles, setProfiles] = useState<Profile[]>([])
  const [loading, setLoading] = useState(false)
  const [recoveredShards, setRecoveredShards] = useState<Record<string, string>>({})
  const [recoveringURL, setRecoveringURL] = useState('')
  const [producingNsec, setProducingNsec] = useState(false)
  const [producedNsec, setProducedNsec] = useState('')
  const [showCreateProfileForm, setShowCreateProfileForm] = useState(false)
  const [creatingProfile, setCreatingProfile] = useState(false)
  const [deletingProfile, setDeletingProfile] = useState('')
  const [sessionExpired, setSessionExpired] = useState(false)
  const [reauthenticating, setReauthenticating] = useState(false)
  const [selectedCentral, setSelectedCentral] = useState<string | null>(null)
  const [settingUp, setSettingUp] = useState(false)

  const auth = useMemo(() => {
    if (!pomegranateAuth?.token || !pomegranateAuth?.centralURL) return null
    return pomegranateAuth
  }, [pomegranateAuth])

  const refresh = useCallback(async () => {
    if (!auth) return
    setLoading(true)
    try {
      const accountResp = await fetch(`${auth.centralURL}/account`, {
        headers: { Authorization: 'Token ' + auth.token }
      })
      if (accountResp.status === 401) {
        setSessionExpired(true)
        setAccount(null)
        setProfiles([])
        return
      }
      if (accountResp.status !== 200) {
        throw new Error(`central /account returned ${accountResp.status}`)
      }
      setAccount((await accountResp.json()) as Account)
      setSessionExpired(false)
      setRecoveredShards({})
      setProducedNsec('')

      const profilesResp = await fetch(`${auth.centralURL}/profiles`, {
        headers: { Authorization: 'Token ' + auth.token }
      })
      if (profilesResp.status !== 200) {
        throw new Error(`central /profiles returned ${profilesResp.status}`)
      }
      setProfiles((await profilesResp.json()) as Profile[])
    } catch (error) {
      toast.error(String(error instanceof Error ? error.message : error))
    } finally {
      setLoading(false)
    }
  }, [auth])

  useEffect(() => {
    void refresh()
  }, [refresh])

  const hasLocalKey =
    currentAccount?.signerType === 'nsec' || currentAccount?.signerType === 'ncryptsec'

  async function reauthenticate() {
    if (!auth) return
    setReauthenticating(true)
    try {
      const token = await authenticateWithGoogle(auth.centralURL)
      setPomegranateAuth({ token, centralURL: auth.centralURL })
    } catch (error) {
      toast.error(error instanceof Error ? error.message : String(error))
    } finally {
      setReauthenticating(false)
    }
  }

  async function setupBunker() {
    if (!selectedCentral || !currentAccount) return

    const secretKey = getLocalSecretKey(currentAccount)
    if (!secretKey) {
      toast.error('could not access your local secret key')
      return
    }

    setSettingUp(true)
    try {
      let target = selectedCentral
      let bunkerUrl: string | null = null
      for (let attempt = 0; attempt < 2; attempt++) {
        try {
          bunkerUrl = await pomegranateSetup(target, secretKey)
          break
        } catch (err) {
          if ((err as any)?.type === TRY_AGAIN && (err as any)?.central) {
            target = (err as any).central
            continue
          }
          throw err
        }
      }
      if (!bunkerUrl) throw new Error('setup failed')
      await bunkerLogin(bunkerUrl)
    } catch (error) {
      toast.error(error instanceof Error ? error.message : String(error))
    } finally {
      setSettingUp(false)
    }
  }

  const recoveredShardCount = Object.keys(recoveredShards).length
  const canProduceNsec = !!account && recoveredShardCount >= (account.threshold ?? Infinity)

  async function recoverShard(operator: AccountOperator) {
    const operatorURL = massageURL(operator.url)
    const popup = window.open(`${operatorURL}/po/recover/google`, 'Recover', 'width=600,height=600')
    if (!popup) {
      toast.error('failed to open recovery popup')
      return
    }

    setRecoveringURL(operator.url)

    try {
      const shard = await new Promise<string>((resolve, reject) => {
        const popupMonitor = window.setInterval(() => {
          if (!popup.closed) return
          cleanup()
          reject(new Error('recovery cancelled'))
        }, 250)

        window.addEventListener('message', handler)

        function cleanup() {
          window.removeEventListener('message', handler)
          window.clearInterval(popupMonitor)
        }

        function handler(event: MessageEvent) {
          if (
            event.origin !== operatorURL ||
            event.source !== popup! ||
            typeof event.data !== 'string'
          ) {
            return
          }

          cleanup()
          popup!.close()
          resolve(event.data)
        }
      })

      if (!shard.startsWith(operator.pubshard)) {
        throw new Error('Recovered shard does not match operator')
      }

      setRecoveredShards((current) => ({ ...current, [operator.url]: shard }))
      setProducedNsec('')
      toast.success('Shard recovered')
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'recovery failed')
    } finally {
      setRecoveringURL('')
    }
  }

  async function produceNsec() {
    if (!account) return

    const shards = account.operators
      .map((operator) => recoveredShards[operator.url])
      .filter((shard): shard is string => Boolean(shard))
      .slice(0, account.threshold)

    if (shards.length < account.threshold) {
      toast.error('need more recovered shards')
      return
    }

    setProducingNsec(true)

    try {
      const secretKey = numberToBytesBE(
        aggregateSecretKeyShards(shards.map((shard) => decodeShard(hexToBytes(shard)))),
        32
      )
      const pubkey = getPublicKey(secretKey)
      if (pubkey !== account.pubkey) {
        throw new Error('Recovered shards produced wrong key')
      }

      setProducedNsec(nsecEncode(secretKey))
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Failed to produce nsec')
    } finally {
      setProducingNsec(false)
    }
  }

  async function createProfile(name: string) {
    if (!auth) return

    if (!name.trim()) {
      toast.error('name is required')
      return
    }

    setCreatingProfile(true)

    try {
      const resp = await fetch(`${auth.centralURL}/profiles`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: 'Token ' + auth.token
        },
        body: JSON.stringify({ name: name.trim() })
      })

      if (!resp.ok) {
        throw new Error(`profile creation failed (${resp.status})`)
      }

      setShowCreateProfileForm(false)
      await refresh()
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'profile creation failed')
    } finally {
      setCreatingProfile(false)
    }
  }

  async function deleteProfile(profile: Profile) {
    if (!auth) return

    if (!confirm(`Delete profile '${profile.name}'?`)) {
      return
    }

    setDeletingProfile(profile.handler_pubkey)

    try {
      const resp = await fetch(`${auth.centralURL}/profiles/${profile.handler_pubkey}`, {
        method: 'DELETE',
        headers: { Authorization: 'Token ' + auth.token }
      })

      if (!resp.ok) {
        throw new Error(`profile deletion failed (${resp.status})`)
      }

      await refresh()
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'profile deletion failed')
    } finally {
      setDeletingProfile('')
    }
  }

  return (
    <SecondaryPageLayout ref={ref} index={index} title="Hosted Bunker Management">
      <div className="space-y-4 my-3 px-4">
        {!auth && (
          <div className="space-y-3">
            <p className="text-sm text-muted-foreground">
              Set up a hosted bunker for the key you have logged in with. Your key will be split
              into shards held by different Pomegranate operators, and signing will be delegated to
              a bunker.
            </p>

            {hasLocalKey ? (
              <>
                <div className="space-y-2">
                  <div className="text-sm font-medium">Choose your central server</div>
                  {CENTRAL_OPTIONS.map((url) => {
                    const selected = selectedCentral === url
                    return (
                      <Button
                        key={url}
                        variant={selected ? 'default' : 'outline'}
                        className="w-full justify-start"
                        disabled={settingUp}
                        onClick={() => setSelectedCentral(url)}
                      >
                        {selected && <HugeiconsIcon icon={CheckIcon} size={16} />}
                        {url.replace(/^https?:\/\//, '')}
                      </Button>
                    )
                  })}
                </div>
                <Button
                  className="w-full"
                  disabled={!selectedCentral || settingUp}
                  onClick={() => void setupBunker()}
                >
                  {settingUp ? 'Setting up...' : 'Log in with Google and set up your bunker'}
                </Button>
              </>
            ) : (
              <p className="text-sm text-muted-foreground">
                Log in with an nsec or ncryptsec first to set up a hosted bunker here.
              </p>
            )}

            <a
              href="https://pomegranate-admin.netlify.app"
              target="_blank"
              rel="noreferrer"
              className="text-xs text-muted-foreground underline"
            >
              Full Pomegranate admin tool
            </a>
          </div>
        )}

        {auth && (
          <div className="space-y-4">
            {sessionExpired && !loading && (
              <div className="border rounded p-3 space-y-2 bg-muted/40">
                <div className="text-sm">Your hosted bunker session has expired.</div>
                <Button
                  className="w-full"
                  disabled={reauthenticating}
                  onClick={() => void reauthenticate()}
                >
                  {reauthenticating ? 'Logging in...' : 'Log in with Google'}
                </Button>
              </div>
            )}
            <div className="flex items-center justify-between gap-2">
              <div className="text-sm text-muted-foreground">
                {account ? `Account for ${account.email}` : loading ? 'Loading...' : 'No account'}
                {account && (
                  <span className="block font-mono break-all text-xs mt-1">
                    {npubEncode(account.pubkey)}
                  </span>
                )}
              </div>
            </div>

            {loading && <div className="text-sm text-muted-foreground">Loading...</div>}

            {account && (
              <div className="space-y-4">
                <div>
                  <div className="font-medium mb-1">
                    Operators ({account.operators.length}, threshold {account.threshold})
                  </div>
                  <div className="space-y-2">
                    {account.operators.map((operator) => {
                      const recovered = !!recoveredShards[operator.url]
                      return (
                        <div
                          key={operator.url}
                          className="border rounded p-2 bg-background space-y-2"
                        >
                          <div className="text-sm break-all">{operator.url}</div>
                          <div className="flex gap-2">
                            <Button
                              className="flex-1"
                              variant={recovered ? 'secondary' : 'outline'}
                              disabled={recoveringURL !== '' || recovered}
                              onClick={() => void recoverShard(operator)}
                            >
                              {recovered
                                ? 'Recovered'
                                : recoveringURL === operator.url
                                  ? 'Recovering...'
                                  : 'Recover'}
                            </Button>
                          </div>
                        </div>
                      )
                    })}
                  </div>
                </div>

                <div className="space-y-2">
                  <div className="text-sm text-muted-foreground">
                    Recovered shards: {recoveredShardCount} / {account.threshold}
                  </div>
                  <Button
                    className="w-full"
                    disabled={!canProduceNsec || producingNsec || !!producedNsec}
                    onClick={() => void produceNsec()}
                  >
                    {producingNsec
                      ? 'Producing nsec...'
                      : producedNsec
                        ? 'nsec recovered'
                        : 'Recover nsec'}
                  </Button>
                  {producedNsec && (
                    <div className="border bg-muted p-2 font-mono text-sm break-all">
                      {producedNsec}
                    </div>
                  )}
                </div>

                <div>
                  <div className="font-medium mb-1">Profiles</div>
                  {profiles.length === 0 && (
                    <div className="text-sm text-muted-foreground mb-2">No profiles yet.</div>
                  )}
                  <div className="space-y-2">
                    {profiles.map((profile) => {
                      const isCurrent = currentBunkerPubkey === profile.handler_pubkey
                      return (
                        <div
                          key={profile.handler_pubkey}
                          className="border rounded p-2 bg-background space-y-1"
                        >
                          <div className="flex items-center justify-between gap-2">
                            <div className="font-medium flex items-center gap-2">
                              {profile.name}
                              {isCurrent && (
                                <span className="text-xs text-muted-foreground border rounded-full px-2 py-0.5">
                                  current
                                </span>
                              )}
                            </div>
                            {!isCurrent && (
                              <Button
                                variant="destructive"
                                size="sm"
                                disabled={deletingProfile !== ''}
                                onClick={() => void deleteProfile(profile)}
                              >
                                {deletingProfile === profile.handler_pubkey
                                  ? 'Deleting...'
                                  : 'Delete'}
                              </Button>
                            )}
                          </div>
                          <div className="text-xs text-muted-foreground break-all font-mono">
                            bunker://{profile.handler_pubkey}?relay=
                            {encodeURIComponent(auth.centralURL.replace(/^http/, 'ws'))}
                          </div>
                        </div>
                      )
                    })}
                  </div>
                  {showCreateProfileForm ? (
                    <CreateProfileForm
                      creating={creatingProfile}
                      onCancel={() => setShowCreateProfileForm(false)}
                      onCreate={(name) => void createProfile(name)}
                    />
                  ) : (
                    <Button className="mt-2" onClick={() => setShowCreateProfileForm(true)}>
                      Create new signing profile
                    </Button>
                  )}
                </div>
              </div>
            )}

            <div className="text-xs text-muted-foreground">
              <a
                href="https://pomegranate-admin.netlify.app"
                target="_blank"
                rel="noreferrer"
                className="underline"
              >
                Full Pomegranate admin tool
              </a>{' '}
              for other functionality, such as account reset and erasing data from operators.
            </div>
          </div>
        )}
      </div>
    </SecondaryPageLayout>
  )
})
PomegranatePage.displayName = 'PomegranatePage'
export default PomegranatePage

function CreateProfileForm({
  creating,
  onCancel,
  onCreate
}: {
  creating: boolean
  onCancel: () => void
  onCreate: (name: string) => void
}) {
  const [name, setName] = useState('')

  return (
    <form
      className="border rounded p-2 bg-background space-y-2"
      onSubmit={(e) => {
        e.preventDefault()
        if (name.trim()) onCreate(name)
      }}
    >
      <Input
        type="text"
        placeholder="Profile name"
        value={name}
        onChange={(e) => setName(e.target.value)}
      />
      <div className="flex gap-2">
        <Button type="submit" className="flex-1" disabled={creating || !name.trim()}>
          {creating ? 'Creating profile...' : 'Create profile'}
        </Button>
        <Button type="button" variant="secondary" className="flex-1" onClick={onCancel}>
          Cancel
        </Button>
      </div>
    </form>
  )
}
